import { getAuthHeader, forceRefreshMicrosoftToken, TokenProviderConfig } from "./TokenProvider";
import { isMockMode, mockRequest } from "../mock/mockInterceptor";

export enum HttpVerb {
  GET = "GET",
  POST = "POST",
  PATCH = "PATCH"
}
export enum ContentType {
  Json = "application/json",
  Http = "multipart/form-data"
}

export interface HttpSuccess {
  status: number;
  responseText: string;
}

export interface HttpError {
  status: number;
  responseText: string;
}

export function isHttpError(err: unknown): err is HttpError {
  return typeof err === "object" && err !== null && "status" in err && "responseText" in err;
}

let authConfig: TokenProviderConfig | null = null;

export function setRequestAuthConfig(config: TokenProviderConfig | null): void {
  authConfig = config;
}

/**
 * Low-level XHR helper. Resolves with { status, responseText } on success,
 * rejects with { status, responseText } on HTTP error, or Error on cancel/network.
 */
function sendHttpRequestInternal(
  method: HttpVerb,
  url: string,
  contentType?: ContentType,
  token?: string | null,
  data?: Object
): { promise: Promise<HttpSuccess>; cancel: () => void } {
  let cancelled = false;
  let cancelFn: () => void = () => {};

  const promise = new Promise<HttpSuccess>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);

    if (contentType) {
      xhr.setRequestHeader("Content-Type", contentType);
    }

    if (token) {
      xhr.setRequestHeader("Authorization", token);
    }

    xhr.setRequestHeader("Accept-Language", "en-US,en;q=0.9,de-DE;q=0.8,de;q=0.7");

    // Prevent the request from hanging indefinitely (e.g. unreachable Odoo server).
    xhr.timeout = 15000;
    xhr.ontimeout = function () {
      if (cancelled) return;
      reject({ status: 0, responseText: "Request timed out" });
    };

    xhr.onload = function () {
      if (cancelled) return;
      const status = xhr.status;
      const responseText = xhr.responseText ?? xhr.response ?? "";
      if (status >= 200 && status < 300) {
        resolve({ status, responseText });
      } else {
        reject({ status, responseText });
      }
    };

    xhr.onerror = function () {
      if (cancelled) return;
      reject({ status: 0, responseText: xhr.responseText ?? xhr.response ?? "" });
    };

    if (contentType === ContentType.Json) {
      xhr.send(JSON.stringify(data || {}));
    } else if (contentType === ContentType.Http) {
      const formData = new FormData();
      for (const prop in data) {
        formData.append(prop, data[prop]);
      }
      xhr.send(formData);
    }

    cancelFn = () => {
      cancelled = true;
      xhr.abort();
      reject(new Error("Request cancelled"));
    };
  });

  return { promise, cancel: () => cancelFn() };
}

export interface RequestWithAuthOptions {
  /** When true, skip auth header and 401-retry (e.g. IAP enrichment) */
  unauthenticated?: boolean;
}

/**
 * Primary request API. Resolves auth via TokenProvider, retries once on 401 after refresh,
 * force-logs out on auth failure. Returns { status, responseText }.
 */
export function requestWithAuth(
  method: HttpVerb,
  url: string,
  contentType?: ContentType,
  data?: Object,
  options?: RequestWithAuthOptions
): { promise: Promise<HttpSuccess>; cancel: () => void } {
  const unauthenticated = options?.unauthenticated ?? false;

  // In mock/demo mode every request is handled locally — no real XHR.
  // A small random delay makes loading spinners appear naturally.
  if (isMockMode()) {
    const responseText = mockRequest(url, data) ?? "{}";
    const ms = 150 + Math.floor(Math.random() * 350); // 150–500 ms
    let timeoutId: ReturnType<typeof setTimeout>;
    let cancelFn: () => void = () => {};
    const promise = new Promise<HttpSuccess>((resolve, reject) => {
      cancelFn = () => {
        clearTimeout(timeoutId);
        reject(new Error("Request cancelled"));
      };
      timeoutId = setTimeout(() => resolve({ status: 200, responseText }), ms);
    });
    return { promise, cancel: () => cancelFn() };
  }

  const executeRequest = (authToken: string | null): { promise: Promise<HttpSuccess>; cancel: () => void } => {
    return sendHttpRequestInternal(method, url, contentType, authToken, data);
  };

  let activeCancel: (() => void) | null = null;

  const promise = (async (): Promise<HttpSuccess> => {
    let token: string | null = null;
    if (!unauthenticated && authConfig) {
      token = getAuthHeader(authConfig);
    }

    const first = executeRequest(token);
    activeCancel = first.cancel;

    try {
      const result = await first.promise;
      return result;
    } catch (err) {
      if (err instanceof Error && err.message === "Request cancelled") {
        throw err;
      }
      if (!unauthenticated && authConfig && isHttpError(err) && err.status === 401) {
        const authMethod = authConfig.getAuthMethod?.() ?? "api-key";
        if (authMethod === "microsoft") {
          try {
            const newToken = await forceRefreshMicrosoftToken(authConfig);
            const second = executeRequest(newToken);
            activeCancel = second.cancel;
            const result = await second.promise;
            return result;
          } catch (refreshErr) {
            // Only force logout on auth failure: refresh failed OR retry returned 401/403
            const isAuthError =
              !isHttpError(refreshErr) || refreshErr.status === 401 || refreshErr.status === 403;
            if (isAuthError) {
              authConfig.forceLogout("Session expired. Please sign in again.");
            }
            throw refreshErr;
          }
        } else {
          authConfig.forceLogout("Session expired. Please sign in again.");
          throw err;
        }
      }
      throw err;
    }
  })();

  return {
    promise,
    cancel: () => {
      if (activeCancel) activeCancel();
    }
  };
}
